import {
    AccessSegment,
    ColumnMetadata,
    ColumnTypes,
} from "~/components/DataGrid";
import { Emdash } from "~/components/Punctuation";
import { AssetColumns } from "~/modules/assets/types";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { DataGridDollarPercentChangeComboCell } from "~/components/DataGrid/DataGridDollarPercentChangeComboCell";
import { DataGridNumberPercentChangeComboCell } from "~/components/DataGrid/DataGridNumberPercentChangeComboCell";
import { AssetTableRowData } from "../types";
import { CatalogMetricSlugs } from "~/modules/catalog/types";

export const DAILY_TRANSFER_VOLUME_TOKEN_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.dailyTransferVolumeToken,
    label: "Daily Transfer Volume (Token)",
    tooltip: CatalogMetricSlugs.daily_transfer_volume_token,
    type: ColumnTypes.number,
    sortsOn: AssetColumns.dailyTransferVolumeToken,
    width: 200,
    render: (item) => {
        const measure = getMeasureValue(item, "daily_transfer_volume_token");

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridNumberPercentChangeComboCell
                value={measure.val}
                pct_change_interval="7d"
                pct_change_value={measure.chg_30d_pct}
                significantDigits={2}
            />
        );
    },
};

export const DAILY_TRANSFER_VOLUME_DOLLAR_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.dailyTransferVolumeDollar,
    label: "Daily Transfer Volume",
    tooltip: CatalogMetricSlugs.daily_transfer_volume_dollar,
    type: ColumnTypes.number,
    sortsOn: AssetColumns.dailyTransferVolumeDollar,
    width: 180,
    render: (item) => {
        const measure = getMeasureValue(item, "daily_transfer_volume_dollar");

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="7d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const TRAILING_7_DAY_TRANSFER_VOLUME_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: CatalogMetricSlugs.trailing_7_day_transfer_volume,
    label: "Weekly Transfer Volume",
    tooltip: CatalogMetricSlugs.trailing_7_day_transfer_volume,
    type: ColumnTypes.number,
    sortsOn: AssetColumns.trailing7DayTransferVolume,
    width: 180,
    render: (item) => {
        const measure = getMeasureValue(item, "trailing_7_day_transfer_volume");

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="7d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const WEEKLY_TRANSFER_VOLUME_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.weeklyTransferVolume,
    label: "Weekly Transfer Volume",
    tooltip: CatalogMetricSlugs.weekly_transfer_volume,
    type: ColumnTypes.number,
    sortsOn: AssetColumns.weeklyTransferVolume,
    width: 180,
    render: (item) => {
        const measure = getMeasureValue(item, "weekly_transfer_volume");

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="7d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: CatalogMetricSlugs.trailing_30_day_transfer_volume,
    label: (
        <>
            Monthly
            <br />
            Transfer Volume
        </>
    ),
    tooltip: CatalogMetricSlugs.trailing_30_day_transfer_volume,
    type: ColumnTypes.number,
    sortsOn: AssetColumns.trailing30DayTransferVolume,
    width: 200,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        const measure = getMeasureValue(
            item,
            "trailing_30_day_transfer_volume",
        );

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};

export const MONTHLY_TRANSFER_VOLUME_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.monthlyTransferVolume,
    label: "Monthly Transfer Volume",
    tooltip: CatalogMetricSlugs.monthly_transfer_volume,
    type: ColumnTypes.number,
    sortsOn: AssetColumns.monthlyTransferVolume,
    width: 200,
    render: (item) => {
        const measure = getMeasureValue(item, "monthly_transfer_volume");

        if (measure === null) {
            return <Emdash />;
        }

        return (
            <DataGridDollarPercentChangeComboCell
                value={measure.val}
                pct_change_interval="30d"
                pct_change_value={measure.chg_30d_pct}
            />
        );
    },
};
